import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import SearchInput from "../../../components/search-input/search-input";
import { useGetInvoiceGroupsQuery, useGetInvoiceGroupDetailsQuery, useGetCompaniesQuery, useCreateInvoiceGroupMutation } from "../endpoints/billing.endpoints";
import StyledDropdown from 'src/components/styled-dropdown';
import InvoiceTableRefactored from '../components/invoice-table-refactored';
import { Button } from 'src/components/ui/button';
import { Plus } from 'lucide-react';
import { canSyncInvoice } from '../utils/billing-utils';
import { ReduxState } from 'src/redux/redux-state';
import CreateBillingCycleDrawer from '../components/create-billing-cycle-drawer';

function getLastMonthAndYear(): { month: number; year: number } {
  const now = new Date();
  const lastMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  return { month: lastMonth, year };
}

const InvoicesTab: React.FC = () => {
  const [searchValue, setSearchValue] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [isGeneratingInvoices, setIsGeneratingInvoices] = useState(false);
  const defaultMonthYear = useMemo(getLastMonthAndYear, []);
  const [createCycleDrawerOpen, setCreateCycleDrawerOpen] = useState(false);
  const [createCycleMonth, setCreateCycleMonth] = useState(defaultMonthYear.month);
  const [createCycleYear, setCreateCycleYear] = useState(defaultMonthYear.year);

  // API queries
  const { data: invoiceGroupsData, refetch: refetchInvoiceGroups } = useGetInvoiceGroupsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const { data: invoiceGroupDetails, isLoading: isLoadingInvoiceGroupDetails, isFetching: isFetchingInvoiceGroupDetails, refetch: refetchInvoiceGroupDetails } = useGetInvoiceGroupDetailsQuery(
    selectedGroupId,
    {
      skip: !selectedGroupId,
      refetchOnMountOrArgChange: true,
    }
  );

  // Silent auto-refresh every 20s when tab is visible (no loading UI, avoids refetch while user is in another tab)
  const AUTO_REFRESH_MS = 20000;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!selectedGroupId) return;
    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        refetchInvoiceGroupDetails();
      }
    };
    intervalRef.current = setInterval(tick, AUTO_REFRESH_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [selectedGroupId, refetchInvoiceGroupDetails]);

  const { data: companies } = useGetCompaniesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [createInvoiceGroup, { isLoading: isCreatingInvoiceGroup }] = useCreateInvoiceGroupMutation();
  const userState = useSelector((state: ReduxState) => state.userState);
  const isBillingAdmin = canSyncInvoice(userState.permissions?.groups ?? []);

  // Sort invoice groups by most recent first
  const sortedInvoiceGroups = (invoiceGroupsData || [])
    .slice()
    .sort((a, b) => {
      if (a.year !== b.year) {
        return b.year - a.year;
      }
      return b.month - a.month;
    });

  // Auto-select the most recent invoice group by default when data loads
  useEffect(() => {
    const sorted = (invoiceGroupsData || [])
      .slice()
      .sort((a, b) => (a.year !== b.year ? b.year - a.year : b.month - a.month));
    if (sorted.length > 0 && !selectedGroupId) {
      setSelectedGroupId(sorted[0].id);
    }
  }, [invoiceGroupsData, selectedGroupId]);

  const formatMonthYear = (month: number, year: number): string => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    return `${monthNames[month - 1]} ${year}`;
  };

  // Dropdown options: existing invoice groups only (no create option)
  const getDropdownOptions = () => {
    return sortedInvoiceGroups.map(group => ({
      value: group.id,
      label: formatMonthYear(group.month, group.year),
      subtitle: `${group.month}/${group.year}`,
    }));
  };

  const handleDropdownSelect = (value: string) => {
    setSelectedGroupId(value);
  };

  const openCreateCycleDrawer = () => {
    const { month, year } = getLastMonthAndYear();
    setCreateCycleMonth(month);
    setCreateCycleYear(year);
    setCreateCycleDrawerOpen(true);
  };

  const handleCreateBillingCycle = async (month: number, year: number) => {
    try {
      const newInvoiceGroup = await createInvoiceGroup({
        month,
        year,
        isInitial: true,
      }).unwrap();
      setSelectedGroupId(newInvoiceGroup.id);
      setCreateCycleDrawerOpen(false);
      await refetchInvoiceGroups();
      if (refetchInvoiceGroupDetails) {
        await refetchInvoiceGroupDetails();
      }
    } catch (error) {
      console.error('Failed to create invoice group:', error);
    }
  };


  const handleGenerateInvoices = async(groupId: string) => {
    const invoiceGroup = sortedInvoiceGroups.find(group => group.id === groupId);
    if (!invoiceGroup) return;

    setIsGeneratingInvoices(true);

    try {
      // Trigger actual invoice generation with isInitial=false
      await createInvoiceGroup({
        month: invoiceGroup.month,
        year: invoiceGroup.year,
        isInitial: false,
      }).unwrap();
    } catch (error) {
      console.error('Failed to generate invoices:', error);
      setIsGeneratingInvoices(false);
    }
  };

  return (
    <div className="h-full flex flex-col space-y-4">

      <div className="">
        <div className="flex flex-row gap-4 w-full items-center justify-between pb-1">
          <div>
            <SearchInput
              placeholder="Search invoices..."
              value={searchValue}
              onChange={setSearchValue}
              debounceMs={500}
              className="w-96 rounded-full"
              inputClassName='bg-white rounded-full '
            />
          </div>
          <div className="flex items-center gap-2">
            <StyledDropdown
              options={getDropdownOptions()}
              selectedValue={selectedGroupId}
              onSelect={handleDropdownSelect}
              placeholder="Select Month"
              buttonVariant="primary"
              isLoading={!invoiceGroupsData}
              buttonClassName='rounded-full bg-gray-201 text-gray-700'
            />
            {isBillingAdmin && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={openCreateCycleDrawer}
                className="rounded-full h-9 w-9 shrink-0"
                aria-label="Create new billing cycle"
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>

      <CreateBillingCycleDrawer
        open={createCycleDrawerOpen}
        onOpenChange={setCreateCycleDrawerOpen}
        existingGroups={sortedInvoiceGroups}
        selectedMonth={createCycleMonth}
        selectedYear={createCycleYear}
        onMonthChange={setCreateCycleMonth}
        onYearChange={setCreateCycleYear}
        onCreate={handleCreateBillingCycle}
        isCreating={isCreatingInvoiceGroup}
      />

      {/* Content */}
      <div className="flex-1 overflow-auto relative">
        <InvoiceTableRefactored
          searchValue={searchValue}
          selectedGroupId={selectedGroupId}
          invoiceGroupDetails={invoiceGroupDetails}
          isLoadingDetails={isLoadingInvoiceGroupDetails}
          isFetchingDetails={isFetchingInvoiceGroupDetails}
          refetchInvoiceGroups={refetchInvoiceGroupDetails}
          companies={companies}
          isGeneratingInvoices={isGeneratingInvoices}
          onGenerationComplete={() => setIsGeneratingInvoices(false)}
          // onOpenPreviewModal={handleOpenPreviewModal} // TODO: Remove this if we don't need i
          onGenerateInvoices={handleGenerateInvoices}
        />
      </div>
    </div>
  );
};

export default InvoicesTab;